import { useState, useCallback, useEffect, useRef } from 'react';
import usePerRoomState, { stateManager } from './usePerRoomState';
import { useI18n } from '../context/I18nContext';
import logger from '../utils/logger';
import { formatFileSize } from '../utils/fileUtils';
import { parseEmoteCommand } from '../utils/messageUtils';

/**
 * Hook for managing message reply functionality
 * Handles reply state, highlighting, and Matrix protocol formatting
 * Supports per-room state management
 */
export const useReply = (roomId) => {
  const { t } = useI18n();
  const [replyingTo, setReplyingTo] = useState(null);
  const { getState, setState, clearState } = usePerRoomState('reply', roomId);
  const previousRoomId = useRef(roomId);

  // Handle room switching - save current state and restore new room's state
  useEffect(() => {
    logger.debug(`[useReply] Room change detected. Previous: ${previousRoomId.current}, Current: ${roomId}, ReplyingTo:`, replyingTo);
    
    if (previousRoomId.current !== roomId) {
      // Save state for previous room if we had one
      if (previousRoomId.current && replyingTo) {
        logger.debug(`[useReply] Saving reply state for room ${previousRoomId.current}:`, replyingTo);
        // Save to previous room using the singleton state manager
        stateManager.setState('reply', previousRoomId.current, replyingTo);
      }
      
      // Clear current state first
      setReplyingTo(null);
      
      // Then restore state for new room
      if (roomId) {
        const savedState = getState();
        logger.debug(`[useReply] Restoring reply state for room ${roomId}:`, savedState);
        if (savedState) {
          setReplyingTo(savedState);
        }
      }
      
      previousRoomId.current = roomId;
    }
  }, [roomId, replyingTo, getState]);

  // Save state when replyingTo changes (but not during room switches)
  useEffect(() => {
    if (roomId && previousRoomId.current === roomId) {
      logger.debug(`[useReply] Saving current reply state for room ${roomId}:`, replyingTo);
      setState(replyingTo);
    }
  }, [roomId, replyingTo, setState]);

  /**
   * Start replying to a message
   * @param {Object} message - The message object to reply to
   * @param {string} senderName - Name of the message sender
   * @param {string} content - Content of the message being replied to
   */
  const startReply = useCallback((message, senderName, content, msgtype, filename, thumbnailUrl, rawSize) => {
    const isAttachment = ['m.image', 'm.video', 'm.audio', 'm.file'].includes(msgtype);
    const isLocation = msgtype === 'm.location';
    // For attachments and location messages, the 'content' is a JSX object which can't be stored.
    // The reply preview for attachments uses the filename.
    // For location messages, use the body from the message content.
    // For text messages, the content is already a string.
    let displayContent;
    if (isAttachment) {
      displayContent = filename;
    } else if (isLocation) {
      // Extract the plain text body from the message content for location messages
      const msgContent = message.getClearContent?.() || message.getContent();
      displayContent = msgContent?.body || t('MessageBubble.location');
    } else if (msgtype === 'm.emote') {
      // Extract the plain text body from the message content for emote messages
      // The 'content' param is JSX from MessageBubble, which coerces to [object Object] in template literals
      const msgContent = message.getClearContent?.() || message.getContent();
      displayContent = msgContent?.body || '';
    } else {
      displayContent = content;
    }

    setReplyingTo({
      id: message.getId(),
      senderName,
      isAttachment,
      content: displayContent,
      msgtype,
      filename,
      thumbnailUrl,
      size: rawSize ? formatFileSize(rawSize) : null, // Format the size here
    });
  }, [t]);

  /**
   * Cancel the current reply
   */
  const cancelReply = useCallback(() => {
    setReplyingTo(null);
    clearState();
  }, [clearState]);

  /**
   * Restore reply state from sessionStorage (without Matrix event)
   * Used when restoring draft after page refresh
   * @param {Object} replyData - Plain object from storage {id, senderName, content}
   */
  const restoreReplyFromStorage = useCallback((replyData) => {
    if (!replyData || !replyData.id) return;
    
    // Validate Matrix event ID format (should start with $)
    if (typeof replyData.id !== 'string' || !replyData.id.startsWith('$')) {
      logger.warn('[useReply] Invalid event ID in stored reply data', replyData);
      return;
    }
    
    logger.debug('[useReply] Restoring reply from storage', replyData);
    
    setReplyingTo({
      id: replyData.id,
      senderName: replyData.senderName || 'Unknown',
      content: replyData.content || '',
      isAttachment: replyData.isAttachment || false,
      msgtype: replyData.msgtype || null,
      filename: replyData.filename || null,
      thumbnailUrl: replyData.thumbnailUrl || null,
      size: replyData.size || null,
      restoredFromStorage: true,
    });
  }, []);

  /**
   * Get Matrix protocol content for reply
   * @param {string} messageBody - The reply message content
   * @returns {Object} Matrix message content with reply metadata
   */
  const getReplyContent = useCallback((messageBody) => {
    if (!replyingTo) return null;

    if (replyingTo.id) {
      const { body, msgtype } = parseEmoteCommand(messageBody);

      return {
        body,
        msgtype,
        'm.relates_to': {
          'm.in_reply_to': {
            event_id: replyingTo.id,
          },
        },
      };
    }

    logger.error('[useReply] Cannot create reply content - no ID');
    return null;
  }, [replyingTo]);

  return {
    replyingTo,
    startReply,
    cancelReply,
    getReplyContent,
    restoreReplyFromStorage,
  };
};

export default useReply;